import React, { Component, ErrorInfo, ReactNode } from 'react';
import IconError from '@assets/images/icon_error_filled.svg';
import { Button } from '@components/common/Button';
import theme from '@theme/index';
import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import styled, { ThemeProvider } from 'styled-components';

const logger = createLogger(LOG_PREFIX.EXCEPTION);

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  height: 100dvh;

  padding: 2rem;
  gap: 1rem;
  background-color: ${({ theme }) => theme.background};

  p,
  h1 {
    word-break: keep-all;
    margin: 0;
  }
`;

const ErrorTitle = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.red};
`;

const UpdateTitle = styled.h1`
  font-size: 1.5rem;
  color: #04c9db;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  text-align: center;

  color: ${({ theme }) => theme.neutralGray[600]};
`;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isRetrying: false,
    };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    // ChunkLoadError 처리
    if (this.isChunkLoadError(error)) {
      const retryCount = this.getRetryCount();

      if (retryCount < 3) {
        this.incrementRetryCount();
        this.setState({ isRetrying: true });

        // 약간의 딜레이 후 새로고침 (캐시 갱신 시간 확보)
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      // 3회 초과 시 에러 페이지 표시
    }

    // 기존 에러 로깅
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorStack =
      error instanceof Error ? (error.stack ?? 'No stack available') : 'No stack available';
    const formattedMessage = `Error: ${errorMessage}\n\nStack: ${errorStack.replace(/\n/g, '\n')}`;
    if (process.env.NODE_ENV !== 'production') return;

    logger.error('애플리케이션 오류 발생', {
      error: formattedMessage,
      url: window.location.href,
    });
  }

  private isChunkLoadError(error: Error): boolean {
    return (
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading CSS chunk')
    );
  }

  private getRetryCount(): number {
    const count = sessionStorage.getItem('chunkLoadRetryCount');
    return count ? parseInt(count, 10) : 0;
  }

  private incrementRetryCount(): void {
    const count = this.getRetryCount();
    sessionStorage.setItem('chunkLoadRetryCount', String(count + 1));
  }

  private clearRetryCount(): void {
    sessionStorage.removeItem('chunkLoadRetryCount');
  }

  componentDidMount(): void {
    // 정상 로딩되면 retry 카운트 초기화
    this.clearRetryCount();
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, isRetrying, error } = this.state;
    const { children, fallback } = this.props;

    if (isRetrying) {
      return (
        <ThemeProvider theme={theme}>
          <ErrorContainer>
            <UpdateTitle>새로운 버전이 배포되었습니다</UpdateTitle>
            <ErrorMessage>
              업데이트된 기능을 불러오는 중입니다.
              <br />
              잠시 후 자동으로 적용됩니다.
            </ErrorMessage>
            <Button buttonType="action" onClick={this.handleRefresh}>
              지금 새로고침
            </Button>
          </ErrorContainer>
        </ThemeProvider>
      );
    }

    if (hasError && error && this.isChunkLoadError(error)) {
      return (
        <ThemeProvider theme={theme}>
          <ErrorContainer>
            <UpdateTitle>새로운 버전이 배포되었습니다</UpdateTitle>
            <ErrorMessage>
              업데이트된 기능이 있습니다.
              <br />
              새로고침 버튼을 눌러 최신 버전을 불러와 주세요.
            </ErrorMessage>
            <Button buttonType="action" onClick={this.handleRefresh}>
              새로고침
            </Button>
          </ErrorContainer>
        </ThemeProvider>
      );
    }

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <ThemeProvider theme={theme}>
          <ErrorContainer>
            <img src={IconError} alt="error" width={64} height={64} />
            <ErrorTitle>오류가 발생했습니다.</ErrorTitle>
            <ErrorMessage>
              예기치 않은 오류가 발생했습니다. 페이지를 새로고침해 주세요.
              <br />
              오류 정보는 자동으로 수집되어 서비스 개선을 위해 활용됩니다.
              <br />
              <br />
              서비스 이용에 불편을 드려 죄송합니다.
            </ErrorMessage>
            <Button buttonType="action" onClick={this.handleRefresh}>
              페이지 새로고침
            </Button>
          </ErrorContainer>
        </ThemeProvider>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
